import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  cancelProductSubscription,
  getBillingSummary,
} from '../../services/api';
import { useAuthSession } from '../../contexts/AuthSessionContext';
import SettingsMenuButton from '../common/SettingsMenuButton';
import ProductCheckoutModal, {
  calculateProductCheckoutSummary,
  normalizeCheckoutProductKey,
} from './ProductCheckoutModal';
import { ensureActiveSession } from '../../services/session';

import {
  STATUS_META,
  INVOICE_STATUS,
  PAGE_BG,
  CARD_SX,
  PRIMARY_BUTTON_SX,
  SUITE_PRODUCTS,
  formatDate,
  formatCurrency,
  getBillingProductKeyForUiProductKey,
  getUiProductKeyForBillingProductKey,
  formatPaymentMethod,
} from './accountOverview.utils';
import AccountOverviewCancelProductDialog from './AccountOverviewCancelProductDialog';
import AccountOverviewProductsPanel from './AccountOverviewProductsPanel';
import AccountOverviewHero from './AccountOverviewHero';
import AccountOverviewStatusGrid from './AccountOverviewStatusGrid';
import AccountOverviewPaymentsPanel from './AccountOverviewPaymentsPanel';
import AccountOverviewSecurityCard from './AccountOverviewSecurityCard';
import AccountOverviewIfoodCard from './AccountOverviewIfoodCard';

const AccountOverview = () => {
  const navigate = useNavigate();
  const {
    hasProductAccess,
    hasPromptOnlyProductAccess = () => false,
    getProductEntryPath,
    isOwner,
  } = useAuthSession();
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [cancellingProductKey, setCancellingProductKey] = useState(null);
  const [checkoutProductKey, setCheckoutProductKey] = useState(null);
  const [productCancellationTarget, setProductCancellationTarget] = useState(null);

  const loadSummary = async (isRefresh = false) => {
    if (!isRefresh) {
      setState((prev) => ({ ...prev, loading: true, error: '' }));
    }

    try {
      const response = await getBillingSummary();
      setState({ loading: false, error: '', data: response?.data || null });
    } catch (error) {
      const message =
        error?.response?.data?.error || 'Não foi possível carregar os dados.';
      setState({ loading: false, error: message, data: null });
    }
  };

  useEffect(() => {
    loadSummary(false);
  }, []);

  const summary = state.data;
  const canUseIfoodIntegration = ['atendabot', 'cardapio', 'feedback_hero']
    .some((productKey) => hasProductAccess(productKey, { allowTrialRestricted: true }));
  const subscription = summary?.subscription || null;
  const billingExempt = Boolean(summary?.user?.billing_exempt);
  const statusKey = billingExempt
    ? 'active'
    : subscription?.status
      ? String(subscription.status).toLowerCase()
      : 'inactive';
  const statusMeta = STATUS_META[statusKey] || STATUS_META.inactive;

  const periodEnd = formatDate(subscription?.current_period_end);
  const trialEnd = formatDate(subscription?.trial_end);
  const statusDateLabel = statusKey === 'trialing' ? 'Grátis até' : 'Renovação em';
  const statusDateValue = statusKey === 'trialing' ? trialEnd : periodEnd;
  const cancelAtPeriodEnd = Boolean(subscription?.cancel_at_period_end);
  const cancelInfo = cancelAtPeriodEnd
    ? periodEnd !== '-'
      ? `Cancelado. Você ainda tem acesso até ${periodEnd}.`
      : 'Cancelado. Você ainda tem acesso até o fim do período.'
    : null;

  const pendingCancellationByProductKey = useMemo(() => {
    const rows = summary?.product_cancellations || [];
    return rows.reduce((acc, row) => {
      if (row?.product_key && row?.status === 'pending') {
        const uiProductKey =
          getUiProductKeyForBillingProductKey(row.product_key);
        acc[uiProductKey] = row;
      }
      return acc;
    }, {});
  }, [summary?.product_cancellations]);

  const subscriptionCustomPricing = subscription?.custom_pricing || null;
  const fallbackSubscriptionProductKeys = useMemo(() => {
    const fallbackKeys = SUITE_PRODUCTS
      .filter((product) => !product.comingSoon)
      .filter((product) => hasProductAccess(product.key))
      .map((product) => getBillingProductKeyForUiProductKey(product.key))
      .map((productKey) => normalizeCheckoutProductKey(productKey))
      .filter(Boolean);

    return fallbackKeys.includes('atendabot')
      ? fallbackKeys.filter((productKey) => productKey !== 'one_menu')
      : fallbackKeys;
  }, [hasProductAccess]);

  const activeSubscriptionProductKeys = useMemo(() => {
    if (statusKey === 'trialing') {
      return [];
    }

    const sourceProducts = Array.isArray(subscription?.products)
      ? subscription.products
      : Array.isArray(subscriptionCustomPricing?.products)
        ? subscriptionCustomPricing.products
        : [];

    const normalizedSourceKeys = sourceProducts
      .map((product) => normalizeCheckoutProductKey(product?.key || product))
      .filter(Boolean);

    if (normalizedSourceKeys.length) {
      return normalizedSourceKeys.includes('atendabot')
        ? normalizedSourceKeys.filter((productKey) => productKey !== 'one_menu')
        : normalizedSourceKeys;
    }

    return fallbackSubscriptionProductKeys;
  }, [
    fallbackSubscriptionProductKeys,
    statusKey,
    subscription?.products,
    subscriptionCustomPricing?.products,
  ]);

  const activeSubscriptionProductKeySet = useMemo(
    () => new Set(activeSubscriptionProductKeys),
    [activeSubscriptionProductKeys]
  );

  const customPricingByUiProductKey = useMemo(() => {
    const rows = Array.isArray(subscriptionCustomPricing?.products)
      ? subscriptionCustomPricing.products
      : [];
    return rows.reduce((acc, product) => {
      const billingProductKey = normalizeCheckoutProductKey(product?.key);
      if (!billingProductKey) return acc;
      acc[getUiProductKeyForBillingProductKey(billingProductKey)] = product;
      return acc;
    }, {});
  }, [subscriptionCustomPricing?.products]);

  const productsBreakdown = useMemo(() => {
    const hasAtendabotProduct = hasProductAccess('atendabot');
    const items = SUITE_PRODUCTS.map((product) => {
      let status = 'blocked';
      if (product.comingSoon) status = 'coming_soon';
      else if (hasProductAccess(product.key, { allowTrialRestricted: true })) status = 'available';
      const promptOnly =
        product.key === 'atendabot'
        && hasPromptOnlyProductAccess('atendabot')
        && !hasProductAccess('atendabot');
      const includedByAtendabot = product.key === 'cardapio' && hasAtendabotProduct;
      const billingProductKey = getBillingProductKeyForUiProductKey(product.key);
      return {
        ...product,
        status,
        promptOnly,
        includedByAtendabot,
        billingProductKey,
        isBillableActive: activeSubscriptionProductKeySet.has(billingProductKey),
        billingLine: customPricingByUiProductKey[product.key] || null,
      };
    });
    const counts = items.reduce(
      (acc, item) => {
        if (item.status === 'available') acc.available += 1;
        else if (item.status === 'coming_soon') acc.comingSoon += 1;
        else acc.blocked += 1;
        return acc;
      },
      { available: 0, comingSoon: 0, blocked: 0 },
    );
    const priority = (s) => (s === 'available' ? 2 : s === 'blocked' ? 1 : 0);
    items.sort((a, b) => priority(b.status) - priority(a.status));
    return { items, counts };
  }, [
    activeSubscriptionProductKeySet,
    customPricingByUiProductKey,
    hasProductAccess,
    hasPromptOnlyProductAccess,
  ]);

  const invoiceRows = useMemo(
    () => {
      const invoiceList = summary?.invoices ?? [];
      return invoiceList.map((invoice) => {
        const statusKeyLocal = invoice?.status ? String(invoice.status).toLowerCase() : '';
        const statusMetaLocal = INVOICE_STATUS[statusKeyLocal] || {
          label: invoice?.status || '-',
          bg: 'rgba(148,163,184,0.2)',
          color: '#475569',
        };
        const periodStartRaw = invoice?.period_start || null;
        const periodEndRaw = invoice?.period_end || null;
        const periodStartLabel = formatDate(periodStartRaw);
        const periodEndLabel = formatDate(periodEndRaw);
        let periodLabel = '-';

        if (periodStartRaw && periodEndRaw) {
          periodLabel =
            periodStartLabel === periodEndLabel
              ? `Cobrança do dia ${periodStartLabel}`
              : `${periodStartLabel} - ${periodEndLabel}`;
        } else if (periodStartRaw) {
          periodLabel = `Desde ${periodStartLabel}`;
        } else if (periodEndRaw) {
          periodLabel = `Até ${periodEndLabel}`;
        }

        return {
          id: invoice?.stripe_invoice_id || `${invoice?.created_at || 'invoice'}-fallback`,
          date: formatDate(invoice?.paid_at || invoice?.created_at),
          amount: formatCurrency(
            statusKeyLocal === 'paid'
              ? invoice?.amount_paid
              : invoice?.amount_due ?? invoice?.amount_paid,
            invoice?.currency
          ),
          paymentMethod: formatPaymentMethod(invoice),
          period: periodLabel,
          status: statusMetaLocal,
          hostedInvoiceUrl: invoice?.hosted_invoice_url || null,
        };
      });
    },
    [summary?.invoices]
  );

  const getProductCancellationDetails = (product) => {
    if (!product) {
      return null;
    }

    const remainingProductKeys = activeSubscriptionProductKeys
      .filter((productKey) => productKey !== product.billingProductKey);
    const hasRemainingProducts = remainingProductKeys.length > 0;
    const remainingSummary = hasRemainingProducts
      ? calculateProductCheckoutSummary(
          [],
          remainingProductKeys,
          { activeBillableKeys: remainingProductKeys }
        )
      : null;
    const currentMonthlyAmount = subscriptionCustomPricing?.totalAmount;
    const nextMonthlyAmount = remainingSummary?.currentTotalCents || 0;
    const hasCurrentMonthlyAmount =
      currentMonthlyAmount !== null
      && currentMonthlyAmount !== undefined
      && Number.isFinite(Number(currentMonthlyAmount));
    const monthlyReduction =
      hasCurrentMonthlyAmount
        ? Number(currentMonthlyAmount) - nextMonthlyAmount
        : null;

    return {
      hasRemainingProducts,
      currentMonthlyAmount,
      nextMonthlyAmount,
      hasCurrentMonthlyAmount,
      monthlyReduction,
    };
  };

  const openCancelProductDialog = (product) => {
    if (
      !isOwner
      || !product
      || product.status !== 'available'
      || product.includedByAtendabot
      || !product.isBillableActive
    ) {
      return;
    }

    setProductCancellationTarget(product);
  };

  const closeCancelProductDialog = () => {
    if (cancellingProductKey) {
      return;
    }
    setProductCancellationTarget(null);
  };

  const handleConfirmCancelProduct = async () => {
    const product = productCancellationTarget;
    if (!product || cancellingProductKey) return;

    setCancellingProductKey(product.key);
    try {
      const response = await cancelProductSubscription(product.key);
      const accessUntilLabel = formatDate(response?.data?.access_until);
      toast.success(
        accessUntilLabel !== '-'
          ? `${product.name} ficará ativo até ${accessUntilLabel}.`
          : `${product.name} será removido no fim do período.`
      );
      await ensureActiveSession({ force: true }).catch(() => null);
      await loadSummary(true);
      setProductCancellationTarget(null);
    } catch (error) {
      const message =
        error?.response?.data?.error || 'Não foi possível cancelar o produto.';
      toast.error(message);
    } finally {
      setCancellingProductKey(null);
    }
  };

  const productCancellationDetails =
    getProductCancellationDetails(productCancellationTarget);

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 4, md: 6 } }}>
      <SettingsMenuButton
        buttonProps={{
          sx: {
            position: 'fixed',
            top: { xs: 16, md: 22 },
            right: { xs: 16, md: 22 },
            zIndex: 20,
            width: 46,
            height: 46,
            color: '#475569',
            borderRadius: '14px',
            background: 'rgba(15, 23, 42, 0.04)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            transition: 'all 0.25s ease',
            '& svg': { transition: 'transform 0.5s ease' },
            '&:hover': {
              background: 'rgba(8, 145, 178, 0.08)',
              borderColor: 'rgba(8, 145, 178, 0.24)',
              color: '#0891b2',
            },
            '&:hover svg': { transform: 'rotate(45deg)' },
          },
        }}
      />
      <Box sx={{ maxWidth: 1150, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <AccountOverviewHero navigate={navigate} />

          {state.loading ? (
            <Box
              sx={{
                ...CARD_SX,
                p: { xs: 4, md: 6 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                minHeight: 240,
              }}
            >
              <CircularProgress size={32} sx={{ color: '#0891b2' }} />
              <Typography sx={{ color: '#64748b' }}>Carregando dados da conta...</Typography>
            </Box>
          ) : state.error ? (
            <Box
              sx={{
                ...CARD_SX,
                p: { xs: 3, md: 3.5 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                borderColor: 'rgba(220, 38, 38, 0.18)',
                background: 'linear-gradient(135deg, #fff 0%, #fff5f5 100%)',
              }}
            >
              <Typography sx={{ color: '#b91c1c', fontWeight: 500 }}>{state.error}</Typography>
              <Button
                variant="contained"
                onClick={() => loadSummary(true)}
                sx={PRIMARY_BUTTON_SX}
              >
                Tentar novamente
              </Button>
            </Box>
          ) : (
            <>
              <AccountOverviewStatusGrid
                summary={summary}
                statusMeta={statusMeta}
                statusDateLabel={statusDateLabel}
                statusDateValue={statusDateValue}
                subscriptionCustomPricing={subscriptionCustomPricing}
                cancelInfo={cancelInfo}
              />

              <AccountOverviewProductsPanel
                productsBreakdown={productsBreakdown}
                pendingCancellationByProductKey={pendingCancellationByProductKey}
                isOwner={isOwner}
                statusKey={statusKey}
                cancelAtPeriodEnd={cancelAtPeriodEnd}
                cancellingProductKey={cancellingProductKey}
                navigate={navigate}
                getProductEntryPath={getProductEntryPath}
                openCancelProductDialog={openCancelProductDialog}
                setCheckoutProductKey={setCheckoutProductKey}
              />

              <AccountOverviewPaymentsPanel invoiceRows={invoiceRows} />

              {canUseIfoodIntegration && <AccountOverviewIfoodCard navigate={navigate} />}

              <AccountOverviewSecurityCard navigate={navigate} />
            </>
          )}
        </Stack>

        <ProductCheckoutModal
          open={Boolean(checkoutProductKey)}
          initialProductKey={checkoutProductKey}
          activeSubscriptionProductKeys={activeSubscriptionProductKeys}
          activeSubscriptionPricing={subscriptionCustomPricing?.products || []}
          title="Ativar produtos"
          onActivated={() => loadSummary(true)}
          onClose={() => setCheckoutProductKey(null)}
        />
        <AccountOverviewCancelProductDialog
          open={Boolean(productCancellationTarget)}
          product={productCancellationTarget}
          details={productCancellationDetails}
          billingExempt={billingExempt}
          cancelling={Boolean(cancellingProductKey)}
          onClose={closeCancelProductDialog}
          onConfirm={handleConfirmCancelProduct}
        />
      </Box>
    </Box>
  );
};

export default AccountOverview;
